import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import useWorkflowChat from '../hooks/useWorkflowChat';
import useSourceActivity, { ConnectionState } from '../hooks/useSourceActivity';
import { WorkflowPreviewBlock } from '../types';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import TypingIndicator from './TypingIndicator';
import WorkflowPreviewPanel from './WorkflowPreviewPanel';
import { User } from 'common/components/UserAvatar/UserAvatar';
import BEM from 'common/bem';

const classes = BEM.with('ClinicalWorkflows');

// Distance (px) from the bottom within which we consider the user "stuck to the
// bottom" and continue auto-scrolling on new content. Scroll up past this and
// we leave their viewport alone (showing a Jump-to-latest button instead).
const STICKY_BOTTOM_THRESHOLD_PX = 120;

// Each tile is paired with the rule-engine source key the connectivity
// indicator queries against. Keep these in sync with patient_service
// source_activity output and the agent's source list.
const EVENT_TYPES: Array<{
  value: string;
  label: string;
  description: string;
  sourceKey: string;
}> = [
  {
    value: 'Nurse Call',
    label: 'Nurse Call Alarms',
    description: 'Nurse call button alarm workflows',
    sourceKey: 'NURSE_CALL',
  },
  {
    value: 'Patient Monitor',
    label: 'Patient Monitor Alarms',
    description: 'Bedside monitor alarm workflows',
    sourceKey: 'PATIENT_MONITOR',
  },
  {
    value: 'Clinical Orders',
    label: 'Clinical Orders (ORM)',
    description: 'EHR consult, admission, and order events',
    sourceKey: 'HL7_ORM',
  },
  {
    value: 'ADT',
    label: 'Admit / Discharge / Transfer',
    description: 'Patient admit, discharge, and transfer events',
    sourceKey: 'HL7_ADT',
  },
  {
    value: 'Lab Results',
    label: 'Lab & Diagnostic Results',
    description: 'HL7 lab and diagnostic result events',
    sourceKey: 'HL7_ORU',
  },
  {
    value: 'EMS Case',
    label: 'EMS Case Alerts',
    description: 'EMS case alerts from Twiage',
    sourceKey: 'EMS_CASE',
  },
  {
    value: 'Scheduling',
    label: 'Patient Scheduling (SIU)',
    description: 'Appointment scheduling, cancellation, and modification events',
    sourceKey: 'HL7_SIU',
  },
];

const STATE_TOOLTIPS: Record<ConnectionState, string> = {
  connected: 'Connected — recent events received',
  idle: 'Configured but no events in the last 7 days',
  disconnected: 'No events ever recorded for this org',
  unknown: 'Connection status unavailable',
};

function formatLastEventAt(lastEventAt: string | null): string {
  if (!lastEventAt) return '';
  const dt = new Date(lastEventAt);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

type BuildTabProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  client: Record<string, any> | null;
  currentOrganization: { displayName: string; name: string } | null;
  currentOrganizationId: string;
  currentUser: User | null;
};

export default function BuildTab({
  client,
  currentOrganization,
  currentOrganizationId,
  currentUser,
}: BuildTabProps) {
  const messageListRef = useRef<HTMLDivElement>(null);
  const endSentinelRef = useRef<HTMLDivElement>(null);
  // Set while a programmatic smooth-scroll is animating. The browser fires
  // intermediate `scroll` events during the animation; if we react to those
  // by re-computing sticky-bottom, distFromBottom is still large early in
  // the animation and the Jump-to-latest button visibly flickers back on.
  // Holding this flag suppresses the scroll listener until the animation
  // settles (we detect settling by polling distFromBottom).
  const isProgrammaticScrollRef = useRef(false);
  const [isStickyBottom, setIsStickyBottom] = useState(true);

  const orgContext = useMemo(
    () =>
      currentOrganization
        ? { orgName: currentOrganization.displayName, orgToken: currentOrganizationId }
        : null,
    [currentOrganization, currentOrganizationId]
  );

  const { messages, isLoading, activeTool, error, sendMessage, selectOption } = useWorkflowChat(
    client,
    orgContext
  );

  const { activity: sourceActivity } = useSourceActivity(client, currentOrganizationId);

  const activeProgressMessage = useMemo(() => {
    if (!activeTool) return null;
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role !== 'assistant') continue;
      for (let j = msg.segments.length - 1; j >= 0; j--) {
        const seg = msg.segments[j];
        if (seg.kind === 'progress' && seg.data.status === 'started') {
          return seg.data.message ?? null;
        }
      }
    }
    return null;
  }, [activeTool, messages]);

  const latestPreview = useMemo<WorkflowPreviewBlock | null>(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role !== 'assistant') continue;
      for (let j = msg.segments.length - 1; j >= 0; j--) {
        const seg = msg.segments[j];
        if (seg.kind === 'workflow_preview') {
          return seg.data;
        }
      }
    }
    return null;
  }, [messages]);

  // Track whether the user is parked at (or near) the bottom of the chat. The
  // scroll listener is the only thing that flips this off — every time we hear
  // a scroll we recompute distance-from-bottom. When sticky, content updates
  // pin them to the latest; when not sticky, we never auto-scroll on their
  // behalf and instead surface a Jump-to-latest affordance.
  const handleMessageListScroll = useCallback(() => {
    // Ignore scroll events that originate from our own scrollIntoView. We
    // clear the flag once distFromBottom is back near zero (i.e. the
    // animation has reached the sentinel).
    if (isProgrammaticScrollRef.current) {
      const el = messageListRef.current;
      if (el) {
        const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (distFromBottom < STICKY_BOTTOM_THRESHOLD_PX) {
          isProgrammaticScrollRef.current = false;
        }
      }
      return;
    }
    const el = messageListRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setIsStickyBottom(distFromBottom < STICKY_BOTTOM_THRESHOLD_PX);
  }, []);

  const scrollToLatest = useCallback((behavior: ScrollBehavior = 'auto') => {
    // Engage the guard before kicking off scrollIntoView so any intermediate
    // scroll events the browser fires during the smooth animation are
    // ignored. For 'auto' (instant) scrolls the guard clears on the very
    // next scroll event, which is harmless.
    isProgrammaticScrollRef.current = true;
    endSentinelRef.current?.scrollIntoView({ block: 'end', behavior });
    setIsStickyBottom(true);
  }, []);

  // Signature that changes on token append. `messages.length` alone misses
  // token streams (which mutate the existing message); combining with the last
  // message's content length covers both add-message and append-token paths
  // without re-rendering on every parent render.
  const streamSignature = useMemo(() => {
    const last = messages[messages.length - 1];
    return `${messages.length}:${last?.content?.length ?? 0}:${last?.segments?.length ?? 0}`;
  }, [messages]);

  // useLayoutEffect runs before paint so the indicator's mount/unmount and the
  // resulting scroll-to-bottom happen in the same frame — eliminating the
  // visible jump that useEffect would produce. Skips when a programmatic
  // smooth scroll is already in flight (e.g. from Jump-to-latest); otherwise
  // this effect's default `behavior` of 'auto' would instantly override the
  // smooth animation and the 'smooth' argument at the call site becomes dead
  // code.
  useLayoutEffect(() => {
    if (!isStickyBottom) return;
    if (isProgrammaticScrollRef.current) return;
    endSentinelRef.current?.scrollIntoView({ block: 'end' });
  }, [streamSignature, activeTool, isStickyBottom]);

  const lastAssistantIdx = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role !== 'assistant') continue;
      const isProgressOnly =
        msg.segments.length > 0 && msg.segments.every((s) => s.kind === 'progress');
      if (!isProgressOnly) return i;
    }
    return -1;
  })();

  const hasUserRepliedToLast =
    lastAssistantIdx >= 0 && messages.slice(lastAssistantIdx + 1).some((m) => m.role === 'user');

  const showWelcome = messages.length === 0 && !isLoading;

  const lastMsg = messages[messages.length - 1];
  const isInputDisabled =
    !client ||
    activeTool !== null ||
    !!(lastMsg?.role === 'assistant' && lastMsg?.isStreaming) ||
    (isLoading && lastMsg?.role !== 'assistant');

  const handleEventSelect = (eventType: string) => {
    sendMessage(`Configure ${eventType} workflow`);
  };

  return (
    <div className={classes('split')}>
      <div className={classes('chat-pane')}>
        <div
          className={classes('message-list')}
          ref={messageListRef}
          onScroll={handleMessageListScroll}
        >
          {showWelcome && (
            <div className={classes('welcome')}>
              <div className={classes('welcome-icon')}>
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                </svg>
              </div>
              <h3 className={classes('welcome-title')}>
                What type of event would you like to configure?
              </h3>
              <p className={classes('welcome-subtitle')}>
                Select an event type to get started with your workflow configuration.
              </p>
              <div className={classes('welcome-grid')}>
                {EVENT_TYPES.map((evt) => {
                  const entry = sourceActivity[evt.sourceKey];
                  const state: ConnectionState = entry?.state ?? 'unknown';
                  const tooltipBase = STATE_TOOLTIPS[state];
                  const lastSeen = formatLastEventAt(entry?.lastEventAt ?? null);
                  const tooltip = lastSeen ? `${tooltipBase} (last: ${lastSeen})` : tooltipBase;
                  return (
                    <button
                      key={evt.value}
                      className={classes('welcome-btn')}
                      onClick={() => handleEventSelect(evt.value)}
                      disabled={!client}
                      type="button"
                    >
                      <span
                        className={classes('source-dot', { state })}
                        title={tooltip}
                        aria-label={tooltip}
                      />
                      <span className={classes('welcome-btn-label')}>{evt.label}</span>
                      <span className={classes('welcome-btn-desc')}>{evt.description}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {messages.map((msg, idx) => (
            <ChatMessage
              key={msg.id}
              message={msg}
              currentUser={currentUser ?? undefined}
              onSelectOption={selectOption}
              isLastAssistantMessage={idx === lastAssistantIdx}
              hasUserReplied={idx === lastAssistantIdx && hasUserRepliedToLast}
            />
          ))}
          {(isLoading || activeTool !== null) &&
          messages[messages.length - 1]?.role !== 'assistant' ? (
            <TypingIndicator message={activeProgressMessage ?? undefined} />
          ) : null}
          {/* Sentinel anchors scrollIntoView calls to the true bottom of the
              list. Keeping it inside the scrolling container means
              auto-scroll survives indicator mount/unmount transitions. */}
          <div ref={endSentinelRef} className={classes('end-sentinel')} aria-hidden="true" />
        </div>

        {!isStickyBottom && (
          <button
            type="button"
            className={classes('jump-to-latest')}
            onClick={() => scrollToLatest('smooth')}
            aria-label="Jump to latest message"
          >
            ↓ Jump to latest
          </button>
        )}

        {error && !isLoading && (
          <div className={classes('error-bar')}>
            <span>{error}</span>
          </div>
        )}

        <ChatInput onSend={sendMessage} disabled={isInputDisabled} />
      </div>

      <div className={classes('preview-pane')}>
        <WorkflowPreviewPanel preview={latestPreview} />
      </div>
    </div>
  );
}
